import { sortByName, sortAsc } from '../utils/sortOptions';
import { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import api from '../services/api';
import toast from 'react-hot-toast';
import { PrinterIcon, UserCircleIcon, XMarkIcon } from '@heroicons/react/24/outline';
import TermMatrixTables, { termMatrixHTML } from '../components/TermMatrixTables';

const css = `
  @import url('https://fonts.googleapis.com/css2?family=Sora:wght@300;400;500;600;700&family=JetBrains+Mono:wght@400;500&display=swap');
  .gr * { box-sizing:border-box; }
  .gr { font-family:'Sora',sans-serif; color:#1a1d2e; animation:grFade .4s ease both; }
  @keyframes grFade { from{opacity:0;transform:translateY(10px)} to{opacity:1;transform:translateY(0)} }
  .gr-header { display:flex; align-items:flex-start; justify-content:space-between; margin-bottom:1.4rem; flex-wrap:wrap; gap:1rem; }
  .gr-title  { font-size:1.35rem; font-weight:700; letter-spacing:-.02em; color:#0f1123; }
  .gr-sub    { font-size:.76rem; color:#9094a8; margin-top:3px; }
  .btn { display:inline-flex; align-items:center; gap:7px; padding:9px 16px; border:none; border-radius:10px; font-family:'Sora',sans-serif; font-size:.8rem; font-weight:600; cursor:pointer; transition:all .18s; }
  .btn svg { width:15px; height:15px; stroke-width:2; }
  .btn-blue   { background:#3b6ef8; color:white; } .btn-blue:hover  { background:#2d5ef0; }
  .btn-green  { background:#10c98e; color:white; } .btn-green:hover { background:#0db87e; }
  .btn-ghost  { background:#f0f2f7; color:#6b7194; } .btn-ghost:hover { background:#e4e7f0; }
  .btn-purple { background:#8b5cf6; color:white; }  .btn-purple:hover { background:#7c3aed; }
  .btn-sm { padding:6px 11px; font-size:.74rem; }
  .btn:disabled { opacity:.5; cursor:not-allowed; }
  .filter-row { display:flex; gap:10px; margin-bottom:1.2rem; flex-wrap:wrap; align-items:flex-end; }
  .filter-group { display:flex; flex-direction:column; gap:5px; }
  .filter-label { font-size:.68rem; font-weight:700; color:#9094a8; text-transform:uppercase; letter-spacing:.06em; }
  .filter-select,.filter-input { font-family:'Sora',sans-serif; font-size:.8rem; padding:8px 12px; border:1.5px solid #e0e4f0; border-radius:9px; background:white; color:#1a1d2e; outline:none; min-width:150px; }
  .filter-select:focus,.filter-input:focus { border-color:#3b6ef8; }
  .sig-panel { background:white; border-radius:14px; border:1px solid #e8ebf4; padding:16px; margin-bottom:1.2rem; }
  .sig-panel-title { font-size:.82rem; font-weight:700; color:#0f1123; margin-bottom:12px; }
  .sig-grid { display:grid; grid-template-columns:1fr 1fr; gap:16px; }
  .sig-box { border:1.5px dashed #e0e4f0; border-radius:10px; padding:12px; text-align:center; position:relative; }
  .sig-box-label { font-size:.68rem; font-weight:700; color:#9094a8; text-transform:uppercase; letter-spacing:.05em; margin-bottom:8px; }
  .sig-box-hint  { font-size:.68rem; color:#b0b5cc; margin-top:4px; margin-bottom:6px; }
  .sig-preview { max-height:52px; max-width:160px; margin:0 auto 4px; display:block; border-radius:4px; }
  .sig-placeholder { width:80%; height:44px; border-bottom:1.5px solid #c0c5dc; margin:0 auto 4px; display:block; }
  .sig-upload-btn { display:inline-flex; align-items:center; gap:5px; padding:5px 12px; border-radius:7px; border:1.5px solid #3b6ef8; background:#f0f4ff; color:#3b6ef8; font-family:'Sora',sans-serif; font-size:.72rem; font-weight:600; cursor:pointer; margin-bottom:6px; }
  .sig-upload-btn:hover { background:#3b6ef8; color:white; }
  .sig-remove-btn { display:inline-flex; align-items:center; gap:5px; padding:3px 9px; border-radius:6px; border:1.5px solid #fecaca; background:#fef2f2; color:#ef4444; font-family:'Sora',sans-serif; font-size:.7rem; font-weight:600; cursor:pointer; margin-left:6px; }
  .sig-name-input { font-family:'Sora',sans-serif; font-size:.76rem; padding:5px 8px; border:1.5px solid #e0e4f0; border-radius:7px; background:#f8f9fc; color:#1a1d2e; outline:none; width:100%; margin-top:4px; }
  .sig-name-input:focus { border-color:#3b6ef8; background:white; }
  .sig-locked { font-size:.68rem; color:#9094a8; margin-top:4px; margin-bottom:6px; }
  .student-list { background:white; border-radius:16px; border:1px solid #e8ebf4; overflow:hidden; margin-bottom:1.2rem; }
  .sl-head  { display:flex; align-items:center; justify-content:space-between; padding:13px 18px; border-bottom:1px solid #f0f2f8; flex-wrap:wrap; gap:8px; }
  .sl-title { font-size:.86rem; font-weight:600; color:#1a1d2e; }
  .sl-row   { display:flex; align-items:center; justify-content:space-between; padding:11px 18px; border-bottom:1px solid #f5f6fb; cursor:pointer; transition:background .12s; }
  .sl-row:last-child { border-bottom:none; }
  .sl-row:hover  { background:#f5f8ff; }
  .sl-row.active { background:#eef2ff; border-left:3px solid #3b6ef8; }
  .sl-name { font-size:.82rem; font-weight:600; color:#0f1123; }
  .sl-meta { font-size:.72rem; color:#9094a8; margin-top:2px; }
  .sl-pct  { font-family:'JetBrains Mono',monospace; font-size:.88rem; font-weight:700; }
  .gr-loading { display:flex; align-items:center; justify-content:center; height:180px; gap:10px; color:#9094a8; }
  .spinner { width:18px; height:18px; border:2px solid #e4e7f0; border-top-color:#10c98e; border-radius:50%; animation:spin .7s linear infinite; }
  @keyframes spin { to{transform:rotate(360deg);} }
  .gr-empty { text-align:center; padding:3rem 2rem; color:#9094a8; font-size:.82rem; }
  .gr-layout { display:grid; grid-template-columns:280px 1fr; gap:1.2rem; align-items:start; }
  .report-card { background:white; border:2px solid #1a1d2e; border-radius:3px; max-width:860px; margin:0 auto; font-family:'Times New Roman',serif; font-size:13px; color:#111; }
  .rc-header { text-align:center; padding:16px 20px 10px; border-bottom:2px solid #1a1d2e; }
  .rc-logo   { max-height:60px; max-width:180px; margin:0 auto 6px; display:block; }
  .rc-school-name { font-size:1.25rem; font-weight:700; text-transform:uppercase; letter-spacing:.08em; }
  .rc-subtitle    { font-size:.82rem; margin-top:3px; }
  .rc-year        { font-size:.88rem; font-weight:700; margin-top:5px; }
  .rc-student-info { display:grid; grid-template-columns:1fr 1fr; border-bottom:1px solid #1a1d2e; }
  .rc-info-cell   { padding:7px 14px; font-size:.82rem; border-right:1px solid #1a1d2e; }
  .rc-info-cell:nth-child(even) { border-right:none; }
  .rc-info-label  { font-weight:700; margin-right:6px; }
  .rc-subject-section { border-bottom:1px solid #1a1d2e; }
  .rc-subject-name { background:#f0f0f0; padding:6px 14px; font-size:.8rem; font-weight:700; text-transform:uppercase; letter-spacing:.04em; border-bottom:1px solid #1a1d2e; display:flex; justify-content:space-between; align-items:center; flex-wrap:wrap; gap:4px; }
  .rc-pct-badges { display:flex; gap:6px; font-size:.72rem; font-weight:400; flex-wrap:wrap; }
  .rc-pct-badge  { background:white; border:1px solid #ccc; padding:2px 7px; border-radius:3px; white-space:nowrap; }
  .rc-marks-wrap  { overflow-x:auto; }
  .rc-marks-table { width:100%; border-collapse:collapse; font-size:.76rem; }
  .rc-marks-table th { background:#e8e8e8; padding:5px 10px; text-align:center !important; border:1px solid #ccc; font-weight:700; font-size:.7rem; white-space:nowrap; }
  .rc-marks-table td { padding:5px 10px; text-align:center !important; border:1px solid #ddd; }
  .rc-marks-table td.lbl { text-align:left; font-weight:600; white-space:nowrap; }
  .rc-marks-table tr:last-child td { background:#f8f8f8; font-weight:700; }
  .rc-overall { display:flex; align-items:center; justify-content:space-between; padding:9px 14px; border-bottom:1px solid #1a1d2e; font-size:.86rem; flex-wrap:wrap; gap:6px; }
  .rc-overall-label { font-weight:700; }
  .rc-overall-pct   { font-size:1.15rem; font-weight:700; font-family:'JetBrains Mono',monospace; }
  .rc-attendance { border-bottom:1px solid #1a1d2e; }
  .rc-att-title  { background:#f0f0f0; padding:6px 14px; font-size:.8rem; font-weight:700; border-bottom:1px solid #1a1d2e; }
  .rc-att-table  { width:100%; border-collapse:collapse; font-size:.78rem; }
  .rc-att-table th { background:#e8e8e8; padding:5px 12px; text-align:center !important; border:1px solid #ccc; font-weight:700; }
  .rc-att-table td { padding:5px 12px; text-align:center !important; border:1px solid #ddd; }
  .rc-footer { display:grid; grid-template-columns:1fr 1fr; padding:16px 20px 14px; gap:20px; }
  .rc-sig-block { text-align:center; }
  .rc-sig-img   { max-height:48px; max-width:150px; margin:0 auto 4px; display:block; }
  .rc-sig-line  { border-top:1px solid #1a1d2e; padding-top:5px; font-size:.78rem; margin-top:30px; }
  .modal-backdrop { position:fixed; inset:0; background:rgba(10,12,28,.6); backdrop-filter:blur(4px); display:flex; align-items:center; justify-content:center; z-index:99999; padding:20px; }
  .modal { background:white; border-radius:18px; padding:24px; width:100%; max-width:460px; box-shadow:0 24px 60px rgba(0,0,0,.25); animation:moIn .22s ease; }
  @keyframes moIn { from{opacity:0;transform:scale(.94)} to{opacity:1;transform:scale(1)} }
  .modal-head  { display:flex; align-items:center; justify-content:space-between; margin-bottom:16px; }
  .modal-title { font-size:.96rem; font-weight:700; color:#0f1123; }
  .modal-close { width:28px; height:28px; border-radius:7px; border:none; background:#f0f2f7; cursor:pointer; display:flex; align-items:center; justify-content:center; }
  .modal-close svg { width:14px; height:14px; stroke-width:2; }
  .modal-btns  { display:flex; gap:8px; justify-content:flex-end; margin-top:18px; flex-wrap:wrap; }
  /* Mobile cards for report-card tables */
  .rc-marks-cards { display:none; }
  .rc-mc-row { display:flex; justify-content:space-between; align-items:center; padding:6px 14px; font-size:.78rem; border-bottom:1px solid #eee; gap:8px; }
  .rc-mc-row:last-child { border-bottom:none; background:#f8f8f8; font-weight:700; }
  .rc-mc-label { font-weight:600; }
  .rc-att-cards { display:none; padding:4px 14px 8px; }
  .rc-att-mrow { display:flex; justify-content:space-between; align-items:center; padding:6px 0; border-bottom:1px solid #eee; font-size:.78rem; }
  .rc-att-mrow:last-child { border-bottom:none; }
  .rc-att-mlabel { font-weight:600; }
  @media print {
    body * { visibility:hidden !important; }
    .gr-print-area, .gr-print-area * { visibility:visible !important; }
    .gr-print-area { position:fixed; left:0; top:0; width:100%; }
    .report-card { page-break-after:always; }
    .report-card:last-child { page-break-after:avoid; }
  }
  @media (max-width:768px) {
    .gr-title { font-size:1.1rem; }
    .gr-layout { grid-template-columns:1fr; }
    .sig-grid  { grid-template-columns:1fr; }
    .filter-row { flex-direction:column; gap:8px; }
    .filter-group { width:100%; }
    .filter-select,.filter-input { min-width:unset; width:100%; }
    .rc-student-info { grid-template-columns:1fr; }
    .rc-info-cell { border-right:none; border-bottom:1px solid #ddd; }
    .rc-footer { grid-template-columns:1fr; }
    .modal-btns .btn { flex:1; justify-content:center; }
    .rc-marks-wrap { display:none !important; }
    .rc-marks-cards { display:block; }
    .rc-att-table-wrap { display:none !important; }
    .rc-att-cards { display:block; }
  }
`;

const curYear = () => { const y = new Date().getFullYear(); return `${y}-${y+1}`; };
const pctColor = p => p >= 80 ? '#10c98e' : p >= 60 ? '#f59e0b' : '#ef4444';

// ─── Signature uploader using a real button + hidden input ref ────────────────
function SigUploader({ label, sig, onChange }) {
  const fileInputRef = useRef(null);

  const handleButtonClick = () => {
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
      fileInputRef.current.click();
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files && e.target.files[0];
    if (!file) return;
    if (file.size > 300 * 1024) {
      toast.error('Signature image must be under 300KB');
      return;
    }
    const reader = new FileReader();
    reader.onload = (ev) => onChange({ ...sig, img: ev.target.result });
    reader.readAsDataURL(file);
  };

  return (
    <div className="sig-box">
      <input
        ref={fileInputRef}
        type="file"
        accept="image/png,image/jpeg,image/jpg,image/gif"
        style={{ display: 'none' }}
        onChange={handleFileChange}
      />

      <div className="sig-box-label">{label}</div>

      {sig && sig.img
        ? <img src={sig.img} className="sig-preview" alt="signature preview" />
        : <div className="sig-placeholder" />
      }

      <div style={{ display: 'flex', justifyContent: 'center', gap: 6, marginTop: 4, marginBottom: 4 }}>
        <button
          type="button"
          className="sig-upload-btn"
          onClick={handleButtonClick}
        >
          📁 {sig && sig.img ? 'Change Image' : 'Upload Image'}
        </button>
        {sig && sig.img && (
          <button
            type="button"
            className="sig-remove-btn"
            onClick={() => onChange({ ...sig, img: '' })}
          >
            ✕ Remove
          </button>
        )}
      </div>

      <input
        type="text"
        className="sig-name-input"
        placeholder={label === 'Class Teacher' ? "Class teacher's name" : "Principal's name"}
        value={(sig && sig.name) || ''}
        onChange={(e) => onChange({ ...sig, name: e.target.value })}
      />
    </div>
  );
}

// ─── Report card component ────────────────────────────────────────────────────
function ReportCard({ report, schoolInfo, sigs }) {
  const ctSig = sigs && sigs.classTeacherSig;
  const prSig = sigs && sigs.principalSig;

  return (
    <div className="report-card">
      <div className="rc-header">
        {schoolInfo && schoolInfo.logo && <img src={schoolInfo.logo} className="rc-logo" alt="logo" />}
        <div className="rc-school-name">{(schoolInfo && schoolInfo.name) || 'School Name'}</div>
        {schoolInfo && schoolInfo.address && <div className="rc-subtitle">{schoolInfo.address}</div>}
        <div className="rc-year">Academic Year: {report.academicYear} &nbsp;|&nbsp; Progress Report</div>
      </div>

      <div className="rc-student-info">
        <div className="rc-info-cell"><span className="rc-info-label">Name:</span>{report.student.name}</div>
        <div className="rc-info-cell"><span className="rc-info-label">Reg No:</span>{report.student.admissionNo}</div>
        <div className="rc-info-cell"><span className="rc-info-label">Grade:</span>{report.student.class} – {report.student.section}</div>
        <div className="rc-info-cell"><span className="rc-info-label">Roll No:</span>{report.student.rollNo}</div>
      </div>

      {report.mode === 'term' ? (
        <TermMatrixTables matrix={report.termMatrix} />
      ) : (
        (report.subjectResults || []).map(subj => {
          const stats = (report.gradeStats && report.gradeStats[subj.subject]) || {};
          return (
            <div key={subj.subject} className="rc-subject-section">
              <div className="rc-subject-name">
                <span>{subj.subject}</span>
                <div className="rc-pct-badges">
                  <span className="rc-pct-badge">Student: {subj.percentage != null ? subj.percentage : '—'}%</span>
                  {stats.highest != null && <span className="rc-pct-badge">Highest: {stats.highest}%</span>}
                  {stats.average != null && <span className="rc-pct-badge">Average: {stats.average}%</span>}
                </div>
              </div>
              <div className="rc-marks-wrap">
                <table className="rc-marks-table">
                  <thead>
                    <tr>
                      <th style={{ textAlign: 'left' }}>Assessment</th>
                      {subj.entries.map(e => <th key={e.typeName}>{e.typeName}</th>)}
                      <th>Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td className="lbl">Maximum Marks</td>
                      {subj.entries.map(e => <td key={e.typeName}>{e.maxMarks}</td>)}
                      <td>{Number(subj.totalMax)}</td>
                    </tr>
                    <tr>
                      <td className="lbl">Obtained Marks</td>
                      {subj.entries.map(e => (
                        <td key={e.typeName}>{e.isAbsent ? 'Abs' : e.isNA ? 'N/A' : (e.marksObtained != null ? e.marksObtained : '—')}</td>
                      ))}
                      <td>{Number(subj.totalObtained)}</td>
                    </tr>
                  </tbody>
                </table>
              </div>

              {/* Mobile cards: one row per assessment type, obtained/max */}
              <div className="rc-marks-cards">
                {subj.entries.map(e => (
                  <div key={e.typeName} className="rc-mc-row">
                    <span className="rc-mc-label">{e.typeName}</span>
                    <span>{e.isAbsent ? 'Abs' : e.isNA ? 'N/A' : (e.marksObtained != null ? e.marksObtained : '—')} / {e.maxMarks}</span>
                  </div>
                ))}
                <div className="rc-mc-row">
                  <span className="rc-mc-label">Total</span>
                  <span>{Number(subj.totalObtained)} / {Number(subj.totalMax)}</span>
                </div>
              </div>
            </div>
          );
        })
      )}

      <div className="rc-overall">
        <span className="rc-overall-label">Overall Percentage:</span>
        <span className="rc-overall-pct" style={{ color: report.overallPercentage != null ? pctColor(report.overallPercentage) : '#1a1d2e' }}>
          {report.overallPercentage != null ? report.overallPercentage + '%' : '—'}
          {report.mode !== 'term' && (
            <>
              &nbsp;<span style={{ fontSize: '.78rem', fontWeight: 400, fontFamily: 'serif' }}>
                ({Number(report.grandTotalObtained)} / {Number(report.grandTotalMax)})
              </span>
            </>
          )}
        </span>
      </div>

      <div className="rc-attendance">
        <div className="rc-att-title">Attendance Summary</div>
        <div className="rc-att-table-wrap" style={{ overflowX: 'auto' }}>
          <table className="rc-att-table">
            <thead>
              <tr><th>Total Days</th><th>Present</th><th>Absent</th><th>Late</th><th>On Leave</th></tr>
            </thead>
            <tbody>
              <tr>
                <td>{(report.attendance && report.attendance.totalDays) || '—'}</td>
                <td>{(report.attendance && report.attendance.present)   || '—'}</td>
                <td>{(report.attendance && report.attendance.absent)    || '—'}</td>
                <td>{(report.attendance && report.attendance.late)      || '—'}</td>
                <td>{(report.attendance && report.attendance.onLeave)   || '—'}</td>
              </tr>
            </tbody>
          </table>
        </div>
        <div className="rc-att-cards">
          <div className="rc-att-mrow"><span className="rc-att-mlabel">Total Days</span><span>{(report.attendance && report.attendance.totalDays) || '—'}</span></div>
          <div className="rc-att-mrow"><span className="rc-att-mlabel">Present</span><span>{(report.attendance && report.attendance.present) || '—'}</span></div>
          <div className="rc-att-mrow"><span className="rc-att-mlabel">Absent</span><span>{(report.attendance && report.attendance.absent) || '—'}</span></div>
          <div className="rc-att-mrow"><span className="rc-att-mlabel">Late</span><span>{(report.attendance && report.attendance.late) || '—'}</span></div>
          <div className="rc-att-mrow"><span className="rc-att-mlabel">On Leave</span><span>{(report.attendance && report.attendance.onLeave) || '—'}</span></div>
        </div>
      </div>

      <div className="rc-footer">
        <div className="rc-sig-block">
          {ctSig && ctSig.img && <img src={ctSig.img} className="rc-sig-img" alt="Class Teacher Signature" />}
          <div className="rc-sig-line">{(ctSig && ctSig.name) || "Class Teacher's Signature"}</div>
        </div>
        <div className="rc-sig-block">
          {prSig && prSig.img && <img src={prSig.img} className="rc-sig-img" alt="Principal Signature" />}
          <div className="rc-sig-line">{(prSig && prSig.name) || "Principal's Signature & School Stamp"}</div>
        </div>
      </div>
    </div>
  );
}

// ─── Build bulk print HTML ────────────────────────────────────────────────────
function buildBulkPrintHTML(reports, schoolInfo, sigs) {
  const ctSig = (sigs && sigs.classTeacherSig) || {};
  const prSig = (sigs && sigs.principalSig)    || {};

  const sigBlock = (sig, defaultLabel) => `
    <div style="text-align:center;">
      ${sig.img ? `<img src="${sig.img}" style="max-height:48px;max-width:150px;margin:0 auto 4px;display:block;"/>` : `<div style="height:44px;"></div>`}
      <div style="border-top:1px solid #111;padding-top:5px;font-size:11px;margin-top:${sig.img ? 2 : 0}px;">
        ${sig.name || defaultLabel}
      </div>
    </div>`;

  const oneReport = (r) => {
    const overallColor = r.overallPercentage != null
      ? (r.overallPercentage >= 80 ? '#10c98e' : r.overallPercentage >= 60 ? '#f59e0b' : '#ef4444')
      : '#111';

    const subjHTML = r.mode === 'term' ? termMatrixHTML(r.termMatrix) : (r.subjectResults || []).map(subj => {
      const stats = (r.gradeStats && r.gradeStats[subj.subject]) || {};
      const thCols  = subj.entries.map(e => `<th style="background:#e8e8e8;padding:4px 8px;text-align:center;border:1px solid #ccc;font-size:11px;font-weight:700;">${e.typeName}</th>`).join('');
      const maxCols = subj.entries.map(e => `<td style="padding:4px 8px;text-align:center;border:1px solid #ddd;">${e.maxMarks}</td>`).join('');
      const obtCols = subj.entries.map(e => `<td style="padding:4px 8px;text-align:center;border:1px solid #ddd;">${e.isAbsent ? 'Abs' : e.isNA ? 'N/A' : (e.marksObtained != null ? e.marksObtained : '—')}</td>`).join('');
      return `
        <div style="border-bottom:1px solid #111;">
          <div style="background:#f0f0f0;padding:5px 14px;font-size:11px;font-weight:700;text-transform:uppercase;border-bottom:1px solid #111;display:flex;justify-content:space-between;flex-wrap:wrap;gap:4px;">
            <span>${subj.subject}</span>
            <span style="font-weight:400;font-size:10px;">
              Student: ${subj.percentage != null ? subj.percentage : '—'}%
              ${stats.highest != null ? ` | Highest: ${stats.highest}%` : ''}
              ${stats.average != null ? ` | Average: ${stats.average}%` : ''}
            </span>
          </div>
          <table style="width:100%;border-collapse:collapse;font-size:11px;">
            <thead><tr>
              <th style="background:#e8e8e8;padding:4px 8px;text-align:left;border:1px solid #ccc;font-size:11px;font-weight:700;">Assessment</th>
              ${thCols}
              <th style="background:#e8e8e8;padding:4px 8px;text-align:center;border:1px solid #ccc;font-size:11px;font-weight:700;">Total</th>
            </tr></thead>
            <tbody>
              <tr>
                <td style="padding:4px 8px;text-align:left;border:1px solid #ddd;font-weight:600;">Maximum Marks</td>
                ${maxCols}
                <td style="padding:4px 8px;text-align:center;border:1px solid #ddd;background:#f8f8f8;font-weight:700;">${Number(subj.totalMax)}</td>
              </tr>
              <tr>
                <td style="padding:4px 8px;text-align:left;border:1px solid #ddd;font-weight:600;">Obtained Marks</td>
                ${obtCols}
                <td style="padding:4px 8px;text-align:center;border:1px solid #ddd;background:#f8f8f8;font-weight:700;">${Number(subj.totalObtained)}</td>
              </tr>
            </tbody>
          </table>
        </div>`;
    }).join('');

    return `
      <div style="border:2px solid #111;border-radius:3px;max-width:860px;margin:0 auto;font-family:'Times New Roman',serif;font-size:13px;color:#111;page-break-after:always;">
        <div style="text-align:center;padding:14px 20px 10px;border-bottom:2px solid #111;">
          ${schoolInfo && schoolInfo.logo ? `<img src="${schoolInfo.logo}" style="max-height:58px;max-width:170px;margin:0 auto 6px;display:block;"/>` : ''}
          <div style="font-size:18px;font-weight:700;text-transform:uppercase;letter-spacing:.06em;">${(schoolInfo && schoolInfo.name) || 'School Name'}</div>
          ${schoolInfo && schoolInfo.address ? `<div style="font-size:12px;margin-top:3px;">${schoolInfo.address}</div>` : ''}
          <div style="font-size:13px;font-weight:700;margin-top:5px;">Academic Year: ${r.academicYear} &nbsp;|&nbsp; Progress Report</div>
        </div>
        <div style="display:grid;grid-template-columns:1fr 1fr;border-bottom:1px solid #111;">
          <div style="padding:7px 14px;font-size:12px;border-right:1px solid #111;"><strong>Name:</strong> ${r.student.name}</div>
          <div style="padding:7px 14px;font-size:12px;"><strong>Reg No:</strong> ${r.student.admissionNo}</div>
          <div style="padding:7px 14px;font-size:12px;border-right:1px solid #111;border-top:1px solid #ddd;"><strong>Grade:</strong> ${r.student.class} – ${r.student.section}</div>
          <div style="padding:7px 14px;font-size:12px;border-top:1px solid #ddd;"><strong>Roll No:</strong> ${r.student.rollNo}</div>
        </div>
        ${subjHTML}
        <div style="display:flex;align-items:center;justify-content:space-between;padding:8px 14px;border-bottom:1px solid #111;font-size:13px;flex-wrap:wrap;gap:4px;">
          <strong>Overall Percentage:</strong>
          <span style="font-size:17px;font-weight:700;font-family:'Courier New',monospace;color:${overallColor};">
            ${r.overallPercentage != null ? r.overallPercentage + '%' : '—'}
            ${r.mode !== 'term' ? `<span style="font-size:11px;font-weight:400;"> (${Number(r.grandTotalObtained)} / ${Number(r.grandTotalMax)})</span>` : ''}
          </span>
        </div>
        <div style="border-bottom:1px solid #111;">
          <div style="background:#f0f0f0;padding:5px 14px;font-size:12px;font-weight:700;border-bottom:1px solid #111;">Attendance Summary</div>
          <table style="width:100%;border-collapse:collapse;font-size:12px;">
            <thead><tr>
              <th style="background:#e8e8e8;padding:5px 12px;text-align:center;border:1px solid #ccc;font-weight:700;">Total Days</th>
              <th style="background:#e8e8e8;padding:5px 12px;text-align:center;border:1px solid #ccc;font-weight:700;">Present</th>
              <th style="background:#e8e8e8;padding:5px 12px;text-align:center;border:1px solid #ccc;font-weight:700;">Absent</th>
              <th style="background:#e8e8e8;padding:5px 12px;text-align:center;border:1px solid #ccc;font-weight:700;">Late</th>
              <th style="background:#e8e8e8;padding:5px 12px;text-align:center;border:1px solid #ccc;font-weight:700;">On Leave</th>
            </tr></thead>
            <tbody><tr>
              <td style="padding:5px 12px;text-align:center;border:1px solid #ddd;">${(r.attendance && r.attendance.totalDays) || '—'}</td>
              <td style="padding:5px 12px;text-align:center;border:1px solid #ddd;">${(r.attendance && r.attendance.present)   || '—'}</td>
              <td style="padding:5px 12px;text-align:center;border:1px solid #ddd;">${(r.attendance && r.attendance.absent)    || '—'}</td>
              <td style="padding:5px 12px;text-align:center;border:1px solid #ddd;">${(r.attendance && r.attendance.late)      || '—'}</td>
              <td style="padding:5px 12px;text-align:center;border:1px solid #ddd;">${(r.attendance && r.attendance.onLeave)   || '—'}</td>
            </tr></tbody>
          </table>
        </div>
        <div style="display:grid;grid-template-columns:1fr 1fr;padding:16px 20px 14px;gap:20px;">
          ${sigBlock(ctSig, "Class Teacher's Signature")}
          ${sigBlock(prSig, "Principal's Signature & School Stamp")}
        </div>
      </div>`;
  };

  return `<!DOCTYPE html><html><head><title>Grade Reports</title>
    <style>* { box-sizing:border-box; } body { margin:0; padding:20px; background:white; } @page { margin:1.5cm; } @media print { body { padding:0; } }</style>
  </head><body>
    ${reports.map(r => oneReport(r)).join('')}
    <script>window.onload = function() { window.print(); };<\/script>
  </body></html>`;
}

// ─── Main component ───────────────────────────────────────────────────────────
const GradeReport = ({ role }) => {
  const isAdmin   = role === 'admin';
  const apiPrefix = isAdmin ? '/admin' : '/teacher';

  const [classes,         setClasses]         = useState([]);
  const [students,        setStudents]        = useState([]);
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [report,          setReport]          = useState(null);
  const [schoolInfo,      setSchoolInfo]      = useState(null);
  const [loading,         setLoading]         = useState(false);
  const [loadingList,     setLoadingList]     = useState(false);
  const [bulkLoading,     setBulkLoading]     = useState(false);
  const [filterClass,     setFilterClass]     = useState('');
  const [filterSection,   setFilterSection]   = useState('');
  const [academicYear,    setAcademicYear]    = useState(curYear());
  const [showSigPanel,    setShowSigPanel]    = useState(false);
  const [showBulkConfirm, setShowBulkConfirm] = useState(false);
  const [accessError,     setAccessError]     = useState(null);

  const [classTeacherSig, setClassTeacherSig] = useState({ img: '', name: '' });
  const [principalSig,    setPrincipalSig]    = useState({ img: '', name: '' });

  const sigs = { classTeacherSig, principalSig };
  const hasSigs = (classTeacherSig.img || principalSig.img) ? true : false;

  const ctSigMounted = useRef(false);
  const prSigMounted = useRef(false);

  useEffect(() => {
    if (isAdmin) {
      api.get('/admin/classes').then(r => setClasses(r.data || [])).catch(() => {});
      api.get('/admin/settings/school').then(r => {
        setSchoolInfo(r.data || {});
        const s = r.data?.settings || {};
        setPrincipalSig({ img: s.principalSignatureImg || '', name: s.principalSignatureName || '' });
      }).catch(() => {});
    } else {
      api.get('/teacher/classes').then(r => setClasses(r.data.classes || [])).catch(() => {});
      api.get('/teacher/profile').then(r => {
        setSchoolInfo(r.data.school || {});
        setPrincipalSig({ img: r.data.school?.principalSignatureImg || '', name: r.data.school?.principalSignatureName || '' });
      }).catch(() => {});
    }
    api.get(`${apiPrefix}/signature`).then(r => {
      setClassTeacherSig({ img: r.data?.img || '', name: r.data?.name || '' });
    }).catch(() => {});
  }, [isAdmin]);

  // Auto-save class-teacher signature (debounced) whenever it changes
  useEffect(() => {
    if (!ctSigMounted.current) { ctSigMounted.current = true; return; }
    const t = setTimeout(() => {
      api.put(`${apiPrefix}/signature`, classTeacherSig).catch(() => {});
    }, 700);
    return () => clearTimeout(t);
  }, [classTeacherSig.img, classTeacherSig.name]);

  // Auto-save principal signature (debounced) — admin only, editable
  useEffect(() => {
    if (!isAdmin) return;
    if (!prSigMounted.current) { prSigMounted.current = true; return; }
    const t = setTimeout(() => {
      api.put('/admin/settings/school', {
        principalSignatureImg:  principalSig.img,
        principalSignatureName: principalSig.name,
      }).catch(() => {});
    }, 700);
    return () => clearTimeout(t);
  }, [principalSig.img, principalSig.name, isAdmin]);

  useEffect(() => {
    if (filterClass && filterSection) fetchStudentList();
  }, [filterClass, filterSection, academicYear]);

  // ── FIX: handle sections stored as strings ['A','B'] OR objects [{name:'A'}] ──
  const getSections = () => {
    const cls = classes.find(c => String(c._id) === String(filterClass));
    const sections = (cls && cls.sections) || [];
    return sections.map(s => (typeof s === 'string' ? s : s.name || s._id || String(s)));
  };

  const fetchStudentList = async () => {
    try {
      setLoadingList(true);
      setStudents([]);
      setReport(null);
      setSelectedStudent(null);
      setAccessError(null);
      const { data } = await api.get(`${apiPrefix}/grade-report/class/${filterClass}?section=${filterSection}&academicYear=${academicYear}`);
      setStudents(data.students || []);
    } catch (err) {
      if (err.response && err.response.status === 403) {
        setAccessError(err.response.data.message || 'You do not have access to this class');
      } else {
        toast.error('Failed to load students');
      }
    } finally {
      setLoadingList(false);
    }
  };

  const fetchReport = async (student) => {
    try {
      setLoading(true);
      setSelectedStudent(student);
      const { data } = await api.get(`${apiPrefix}/grade-report/${student.studentId}?academicYear=${academicYear}`);
      setReport(data);
    } catch (err) {
      toast.error('Failed to load report');
    } finally {
      setLoading(false);
    }
  };

  const printSingle = () => {
    if (!report) return;
    const win = window.open('', '_blank', 'width=900,height=1100');
    win.document.write(buildBulkPrintHTML([report], schoolInfo, sigs));
    win.document.close();
  };

  const bulkPrint = async () => {
    if (!filterClass || !filterSection) return;
    setBulkLoading(true);
    setShowBulkConfirm(false);
    try {
      toast('Loading all reports…', { icon: '⏳', duration: 3000 });
      const results = await Promise.all(
        students.map(s =>
          api.get(`${apiPrefix}/grade-report/${s.studentId}?academicYear=${academicYear}`)
             .then(r => r.data)
             .catch(() => null)
        )
      );
      const valid = results.filter(Boolean);
      if (!valid.length) { toast.error('No reports found'); return; }
      const win = window.open('', '_blank', 'width=900,height=1100');
      win.document.write(buildBulkPrintHTML(valid, schoolInfo, sigs));
      win.document.close();
      toast.success(`${valid.length} reports sent to printer`);
    } catch {
      toast.error('Failed to load reports');
    } finally {
      setBulkLoading(false);
    }
  };

  return (
    <div className="gr">
      <style>{css}</style>

      <div className="gr-header">
        <div>
          <div className="gr-title">Grade Reports</div>
          <div className="gr-sub">
            {isAdmin ? 'View and print student progress reports' : 'View and print reports for your assigned classes'}
          </div>
        </div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <button
            className={`btn btn-sm ${showSigPanel ? 'btn-green' : 'btn-ghost'}`}
            onClick={() => setShowSigPanel(p => !p)}
          >
            <UserCircleIcon />
            {hasSigs ? '✓ Signatures Set' : 'Add Signatures'}
          </button>
          {filterClass && filterSection && students.length > 0 && (
            <button className="btn btn-purple btn-sm" onClick={() => setShowBulkConfirm(true)} disabled={bulkLoading}>
              <PrinterIcon />
              {bulkLoading ? 'Loading…' : `Print All (${students.length})`}
            </button>
          )}
          {report && (
            <button className="btn btn-blue btn-sm" onClick={printSingle}>
              <PrinterIcon />Print Report
            </button>
          )}
        </div>
      </div>

      {showSigPanel && (
        <div className="sig-panel">
          <div className="sig-panel-title">
            ✍️ Electronic Signatures
            <span style={{ fontSize: '.72rem', fontWeight: 400, color: '#9094a8', marginLeft: 6 }}>
              Optional — if not set, blank lines appear for manual signing
            </span>
          </div>
          <div className="sig-grid">
            <SigUploader
              label="Class Teacher"
              sig={classTeacherSig}
              onChange={setClassTeacherSig}
            />
            {isAdmin ? (
              <SigUploader
                label="Principal"
                sig={principalSig}
                onChange={setPrincipalSig}
              />
            ) : (
              <div className="sig-box">
                <div className="sig-box-label">Principal</div>
                {principalSig.img
                  ? <img src={principalSig.img} className="sig-preview" alt="principal" />
                  : <div className="sig-placeholder" />
                }
                <div className="sig-locked">🔒 Principal signature is set by Admin</div>
                <input
                  type="text"
                  className="sig-name-input"
                  placeholder="Principal's name (optional)"
                  value={principalSig.name || ''}
                  onChange={e => setPrincipalSig(p => ({ ...p, name: e.target.value }))}
                />
              </div>
            )}
          </div>
          {hasSigs && (
            <div style={{ marginTop: 10, fontSize: '.74rem', color: '#10c98e', fontWeight: 600 }}>
              ✓ Signatures will appear on all printed reports
            </div>
          )}
        </div>
      )}

      <div className="filter-row">
        <div className="filter-group">
          <label className="filter-label">Class</label>
          <select className="filter-select" value={filterClass}
            onChange={e => { setFilterClass(e.target.value); setFilterSection(''); setStudents([]); setReport(null); setSelectedStudent(null); setAccessError(null); }}>
            <option value="">Select class…</option>
            {sortByName(classes).map(c => <option key={c._id} value={c._id}>{c.name}</option>)}
          </select>
        </div>
        <div className="filter-group">
          <label className="filter-label">Section</label>
          <select className="filter-select" value={filterSection}
            onChange={e => { setFilterSection(e.target.value); setReport(null); setAccessError(null); }}
            disabled={!filterClass}>
            <option value="">Select section…</option>
            {sortAsc(getSections()).map(s => <option key={s} value={s}>{s}</option>)}
          </select>
        </div>
        <div className="filter-group">
          <label className="filter-label">Academic Year</label>
          <input type="text" className="filter-input" value={academicYear}
            onChange={e => setAcademicYear(e.target.value)} placeholder="2025-2026" />
        </div>
      </div>

      {!filterClass || !filterSection ? (
        <div className="gr-empty">Select a class and section above to view students</div>
      ) : accessError ? (
        <div style={{ background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 14, padding: '2rem', textAlign: 'center', maxWidth: 500, margin: '0 auto' }}>
          <div style={{ fontSize: '2rem', marginBottom: 10 }}>🔒</div>
          <div style={{ fontSize: '.92rem', fontWeight: 700, color: '#ef4444', marginBottom: 6 }}>Access Restricted</div>
          <div style={{ fontSize: '.8rem', color: '#6b7194', lineHeight: 1.6 }}>{accessError}</div>
          {!isAdmin && (
            <div style={{ fontSize: '.74rem', color: '#9094a8', marginTop: 10 }}>
              Only the designated class teacher can view grade reports for this class.
              Contact your administrator if you think this is a mistake.
            </div>
          )}
        </div>
      ) : (
        <div className="gr-layout">
          <div className="student-list">
            <div className="sl-head">
              <div className="sl-title">Students ({students.length})</div>
            </div>
            {loadingList ? (
              <div className="gr-loading"><div className="spinner" />Loading…</div>
            ) : students.length === 0 ? (
              <div style={{ padding: '2rem', textAlign: 'center', color: '#9094a8', fontSize: '.8rem' }}>No students found</div>
            ) : students.map(s => (
              <div
                key={s.studentId}
                className={`sl-row ${selectedStudent && selectedStudent.studentId === s.studentId ? 'active' : ''}`}
                onClick={() => fetchReport(s)}
              >
                <div>
                  <div className="sl-name">{s.name}</div>
                  <div className="sl-meta">Roll: {s.rollNo} · {s.admissionNo}</div>
                </div>
                <div className="sl-pct" style={{ color: s.percentage != null ? pctColor(s.percentage) : '#b0b5cc' }}>
                  {s.percentage != null ? s.percentage + '%' : '—'}
                </div>
              </div>
            ))}
          </div>

          {loading ? (
            <div className="gr-loading"><div className="spinner" />Generating report…</div>
          ) : report ? (
            <div>
              <div style={{ fontSize: '.72rem', color: '#9094a8', marginBottom: 8, display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 8 }}>
                <span>Report for <strong style={{ color: '#0f1123' }}>{report.student.name}</strong></span>
                <button className="btn btn-blue btn-sm" onClick={printSingle}><PrinterIcon />Print This Report</button>
              </div>
              <ReportCard report={report} schoolInfo={schoolInfo} sigs={sigs} />
            </div>
          ) : (
            <div className="gr-empty">Click a student to view their report</div>
          )}
        </div>
      )}

      {showBulkConfirm && createPortal(
        <div className="modal-backdrop" onClick={() => setShowBulkConfirm(false)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <div className="modal-head">
              <div className="modal-title">Print All Reports</div>
              <button className="modal-close" onClick={() => setShowBulkConfirm(false)}><XMarkIcon /></button>
            </div>
            <div style={{ fontSize: '.82rem', color: '#6b7194', lineHeight: 1.6, marginBottom: 8 }}>
              This will print <strong style={{ color: '#0f1123' }}>{students.length} grade reports</strong> for{' '}
              <strong style={{ color: '#0f1123' }}>
                {(classes.find(c => String(c._id) === String(filterClass)) || {}).name} — Section {filterSection}
              </strong>.
            </div>
            <div style={{ fontSize: '.78rem', color: '#6b7194', lineHeight: 1.6 }}>
              Choose <strong>Save as PDF</strong> in the print dialog to download, or <strong>Print</strong> to send to printer.
            </div>
            {hasSigs && (
              <div style={{ marginTop: 10, padding: '8px 12px', background: '#ecfdf5', borderRadius: 9, fontSize: '.76rem', color: '#10c98e', fontWeight: 600 }}>
                ✓ Uploaded signatures will appear on all {students.length} reports
              </div>
            )}
            <div className="modal-btns">
              <button className="btn btn-ghost" onClick={() => setShowBulkConfirm(false)}>Cancel</button>
              <button className="btn btn-purple" onClick={bulkPrint}>
                <PrinterIcon />Print All {students.length} Reports
              </button>
            </div>
          </div>
        </div>
      
      , document.body)}
    </div>
  );
};

export default GradeReport;